import React, { useEffect } from "react";
import TextField from "@mui/material/TextField";
import { useSelector } from "react-redux";

export default function CustomTextFieldWithErrorProps({
  id,
  defaultValue,
  value,
  width,
  fontSize,
  disabled,
  maxLength,
  onChange,
  placeholder,
  permittedCharacters,
  errorText,
  type,
  error,
  helperText,
  FormHelperTextProps,
  inputProps,
}) {
  const themechange = useSelector((state) => state.themedata.theme);
  const [val, setval] = React.useState(defaultValue || "");
  const [internalError, setInternalError] = React.useState("");
  const [permittedCharacter, setPermittedCharacter] = React.useState("");

  const isControlled = value !== undefined;
  const displayValue = isControlled ? value : val;

  const showError = error !== undefined ? error : !!internalError;
  const displayHelperText = helperText !== undefined ? helperText : internalError;

  useEffect(() => {
    if (value === undefined && defaultValue !== undefined) {
      setval(defaultValue);
    }
  }, [defaultValue, value]);

  useEffect(() => {
    if (permittedCharacters) {
      let re = new RegExp(permittedCharacters);
      setPermittedCharacter(re);
    }
  }, [permittedCharacters]);

  return (
    <TextField
      id={id}
      type={type ? type : "text"}
      disabled={disabled != null ? disabled : false}
      required
      placeholder={placeholder ? placeholder : null}
      value={displayValue || ""}
      variant="standard"
      helperText={displayHelperText}
      error={showError}
      FormHelperTextProps={{
        ...FormHelperTextProps,
        sx: {
          fontFamily: "Roboto",
          fontSize: "14px",
          marginTop: "4px",
          ...(FormHelperTextProps?.sx || {}),
        }
      }}
      onChange={(e) => {
        let newValue = e.target.value;

        // **NUMBER VALIDATION - DON'T AUTO-CORRECT, JUST BLOCK**
        if (type === "number" && inputProps) {
          const numValue = parseFloat(newValue);
          
          // Check if value exceeds max - DON'T update if it does
          if (inputProps.max !== undefined && !isNaN(numValue) && numValue > inputProps.max) {
            // Don't update the value, just return without calling onChange
            setInternalError(`Value cannot exceed ${inputProps.max}`);
            return;
          }
          
          // Check if value is below min - DON'T update if it does
          if (inputProps.min !== undefined && !isNaN(numValue) && numValue < inputProps.min) {
            setInternalError(`Value cannot be less than ${inputProps.min}`);
            return;
          }

          // Clear error if value is valid
          if (!isNaN(numValue) && 
              (inputProps.min === undefined || numValue >= inputProps.min) &&
              (inputProps.max === undefined || numValue <= inputProps.max)) {
            setInternalError("");
          }

          // Validate step/decimal places if specified
          if (inputProps.step !== undefined && inputProps.step < 1) {
            const decimalPlaces = inputProps.step.toString().split('.')[1]?.length || 0;
            const regex = new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`);
            if (!regex.test(newValue) && newValue !== '') {
              setInternalError(`Maximum ${decimalPlaces} decimal places allowed`);
              return; // Don't update if decimal places exceed step
            }
          }
        }

        // Existing character validation logic
        if (permittedCharacters) {
          if (newValue.length != 0) {
            if (permittedCharacter.test(newValue)) {
              setInternalError("");
              if (!isControlled) {
                setval(newValue);
              }
              onChange(newValue, e);
            } else {
              setInternalError(errorText || "Invalid input");
            }
          } else {
            setInternalError("");
            if (!isControlled) {
              setval(newValue);
            }
            onChange(newValue, e);
          }
        } else {
          if (!isControlled) {
            setval(newValue);
          }
          onChange(newValue, e);
        }
      }}
      onBlur={(e) => {
        if (type === "number" && inputProps) {
          let finalValue = parseFloat(e.target.value);
          
          // If value is invalid, empty, or out of range after blur, correct it
          if (isNaN(finalValue)) {
            finalValue = inputProps.min || 0;
            const formattedValue = finalValue.toString();
            if (!isControlled) {
              setval(formattedValue);
            }
            onChange(formattedValue, e);
            setInternalError("");
          } else if (inputProps.max !== undefined && finalValue > inputProps.max) {
            // On blur, if value exceeds max, set to max
            const formattedValue = inputProps.max.toString();
            if (!isControlled) {
              setval(formattedValue);
            }
            onChange(formattedValue, e);
            setInternalError("");
          } else if (inputProps.min !== undefined && finalValue < inputProps.min) {
            // On blur, if value is below min, set to min
            const formattedValue = inputProps.min.toString();
            if (!isControlled) {
              setval(formattedValue);
            }
            onChange(formattedValue, e);
            setInternalError("");
          } else {
            // Valid value, clear any errors
            setInternalError("");
          }
        }
      }}
      inputProps={{ 
        maxLength: maxLength ? maxLength : 250,
        ...inputProps
      }}
      sx={{
        width: width ? `${width}px` : "94px",
        "& .MuiInputBase-input:focus": {
          color: "#008CD7",
        },
        "& .MuiInputBase-root": {
          fontFamily: "Roboto",
          fontSize: fontSize ? `${fontSize}px` : "16px",
          fontWeight: "700",
          color: themechange === "light" ? "#3C3C3C" : "#C1C1C1",
        },
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: themechange === "light" ? "#C1C1C1" : "#3C3C3C",
        },
        "& .MuiInput-root .MuiInputBase-input": {
          padding: 0,
        },
        "& .MuiInputBase-root::before": {
          borderBottom: `1px solid ${
            (disabled != null ? disabled : false) == true
              ? themechange === "light"
                ? "#C1C1C1"
                : "#3C3C3C"
              : themechange === "light"
              ? "#3C3C3C"
              : "#C1C1C1"
          }`,
        },
        "& ::-webkit-input-placeholder": {
          color: themechange === "light" ? "#3C3C3C" : "#C1C1C1",
        },
        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
          borderBottom: "2px solid #008CD7",
        },
        "& .MuiInputBase-root::after": {
          borderBottom: "2px solid #008CD7",
        },
        "& .Mui-error": {
          color: "var(--RedBaseColor)",
        },
        "& .MuiFormHelperText-root": {
          marginLeft: 0,
          marginRight: 0,
        },
      }}
    />
  );
}